import React from "react";
import { useLazyQuery } from "@apollo/client";
import { Card, Button, Text } from "@nike/eds";
import { Collapsible } from "@nike/epic-react-ui";
import { Download } from "@nike/nike-design-system-icons";
import { GET_CONSIGNMENT_DOCUMENTS } from "../../graphql/consignment.graphql";
import { CustomsEntryDocument, CustomsStatusDocument, FilingPackDocument } from "../../graphql/graphql";

type ConsignmentDocumentsTabProps = {
  documents: (CustomsEntryDocument | CustomsStatusDocument | FilingPackDocument)[];
  consignmentId: string;
};

export const ConsignmentDocumentsTab: React.FC<ConsignmentDocumentsTabProps> = ({
  documents,
  consignmentId,
}) => {
  const documentsByStep = documents.reduce((acc, doc, idx) => {
    const stepKey = encodeURIComponent(doc.stepTitle || "Unknown Step " + idx);
    if (!acc[stepKey]) {
      acc[stepKey] = [];
    }
    acc[stepKey].push(doc);
    return acc;
  }, {} as Record<string, (CustomsEntryDocument | CustomsStatusDocument | FilingPackDocument)[]>);

  const [fetchDocuments, { loading }] = useLazyQuery(GET_CONSIGNMENT_DOCUMENTS);

  const handleDownload = async (docId: string) => {
    const result = await fetchDocuments({ variables: { id: consignmentId } });
    const docs = result.data?.consignment?.get?.documents || [];
    const found = docs.find(
      (d: CustomsEntryDocument | CustomsStatusDocument | FilingPackDocument) => d.id === docId
    );
    if (found && found.downloadUrl) {
      const link = document.createElement("a");
      link.href = found.downloadUrl;
      link.download = found.fileName || "document";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const stepKeys = Object.keys(documentsByStep);

  return (
    <Card className="eds-spacing--p-24">
      {documents.length === 0 && (
        <Text
          font="title-4"
          as="h4"
          className="eds-spacing--mt-56 eds-spacing--mb-56"
          style={{ textAlign: "center" }}
        >
          No documents found for this consignment.
        </Text>
      )}
      {stepKeys.map((stepKey) => (
        <Collapsible
          title={decodeURIComponent(stepKey)}
          key={stepKey}
          isOpen={stepKeys.length === 1} // Default open if only one step
        >
          <ul>
            {documentsByStep[stepKey].map(
              (doc: CustomsEntryDocument | CustomsStatusDocument | FilingPackDocument, idx) => (
                <li key={idx + doc.id} className="eds-spacing--mb-16">
                  <Button
                    variant="ghost"
                    onClick={() => handleDownload(doc.id)}
                    style={{ padding: 0, minWidth: 0 }}
                    disabled={loading}
                  >
                    {doc.fileName} <Download />
                  </Button>
                  <div>
                    <Text as="span" font="body-3">
                      Type: <strong>{doc.type?.replace(/_/g, " ") || "--"}</strong>
                    </Text>
                  </div>

                  {"customsReferenceNumber" in doc && doc.customsReferenceNumber ? (
                    <div>
                      <Text as="span" font="body-3">Customs Reference Number:</Text>
                      <Text as="span" font="body-3" className="eds-spacing--ml-8">
                        <strong>{doc.customsReferenceNumber}</strong>
                      </Text>
                    </div>
                  ) : (
                    ""
                  )}

                  {"brokerReferenceNumber" in doc && doc.brokerReferenceNumber ? (
                    <div>
                      <Text as="span" font="body-3">Broker Reference Number:</Text>
                      <Text as="span" font="body-3" className="eds-spacing--ml-8">
                        <strong>{doc.brokerReferenceNumber}</strong>
                      </Text>
                    </div>
                  ) : (
                    ""
                  )}

                  {"status" in doc && doc.status ? (
                    <div>
                      <Text as="span" font="body-3">Status:</Text>
                      <Text as="span" font="body-3" className="eds-spacing--ml-8">
                        <strong>{doc.status}</strong>
                      </Text>
                    </div>
                  ) : (
                    ""
                  )}
                </li>
              )
            )}
          </ul>
        </Collapsible>
      ))}
    </Card>
  );
};